<script lang="ts">
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';
	import { editable } from '$lib/storyblok/editable';
	import type { ISbStoryData } from '@storyblok/js';
	import HorizontalLine from '../horizontalLine.svelte';
	import ProductPrice from '$lib/storyblok/parts/productPrice.svelte';
	import { shoppingCart } from '$lib/state/shoppingCart.svelte';
	import ProductSelectorItem from '../../parts/productSelectorItem.svelte';
	import Afterpay from '$lib/icons/afterpay.svelte';
	import Affirm from '$lib/icons/affirm.svelte';
	import Modal from '$lib/modal/v2/modal.svelte';
	import AffirmModalContent from '$lib/modals/bnplModal/affirmModalContent.svelte';
	import AfterpayModalContent from '$lib/modals/bnplModal/afterpayModalContent.svelte';
	import { fade } from 'svelte/transition';

	interface Props {
		content: ISbStoryData<{
			title: string;
			display_affirm?: boolean;
			display_afterpay?: boolean;
		}>['content'];
	}

	const { content }: Props = $props();

	let affirmModal = $state<{ show: () => void; hide: () => void }>();
	let afterpayModal = $state<{ show: () => void; hide: () => void }>();
</script>

{#snippet financingElement(type: 'affirm' | 'afterpay')}
	<button
		type="button"
		class="rounded-xl p-1.5 outline outline-2 outline-transparent hover:outline-brown-5"
		onclick={() => {
			if (type === 'affirm') {
				affirmModal?.show();
			} else {
				afterpayModal?.show();
			}
		}}
	>
		{#if type === 'affirm'}
			<Affirm class="h-4" />
		{:else}
			<Afterpay class="h-4" />
		{/if}
	</button>
{/snippet}

<div use:editable={content}>
	<p class="text-[20px]/[140%] tracking-[-.2px]">{content.title}</p>

	<div class="flex">
		<div class="ml-1 mt-4 grid grid-cols-3 gap-4 lg:gap-5">
			{#if !shoppingCart.isUpdating && shoppingCart.components}
				{#each shoppingCart.components as product}
					{#if product.component === 'product_details'}
						<ProductSelectorItem {product} />
					{/if}
				{/each}
			{:else}
				{#each Array(3) as _}
					<div class="flex w-26 flex-col gap-y-2 lg:w-22">
						<div class="h-15 animate-pulse rounded-lg bg-black/20 lg:h-13"></div>
						<div class="h-[22px] animate-pulse rounded-md bg-black/20"></div>
					</div>
				{/each}
			{/if}
		</div>
	</div>

	<div class="my-6 max-lg:mt-7">
		<HorizontalLine {content} />
	</div>

	<div
		use:editable={content}
		class="mb-1 flex flex-col gap-2 text-[20px]/[140%] tracking-[-.2px] lg:mb-2"
	>
		<div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
			<div
				class={[
					'flex items-center gap-x-1',
					{ contents: !content.display_affirm && !content.display_afterpay }
				]}
			>
				<ProductPrice
					price={checkoutSession.data.product?.price.unit_amount || 99900}
					promoCode={checkoutSession.data.product?.promotionCode}
				/>

				{#if checkoutSession.canUseFinancing && (content.display_affirm || content.display_afterpay)}
					<div class="ml-2" transition:fade>
						{#if content.display_affirm}
							{@render financingElement('affirm')}
						{/if}

						{#if content.display_afterpay}
							{@render financingElement('afterpay')}
						{/if}
					</div>
				{/if}
			</div>
		</div>
	</div>
</div>

{#if checkoutSession.canUseFinancing && content.display_affirm}
	<Modal withDesktopPadding size="regular" modalName="Affirm Modal" bind:this={affirmModal}>
		<AffirmModalContent />
	</Modal>
{/if}

{#if checkoutSession.canUseFinancing && content.display_afterpay}
	<Modal withDesktopPadding size="regular" modalName="Afterpay Modal" bind:this={afterpayModal}>
		<AfterpayModalContent />
	</Modal>
{/if}
